import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * REPORTING-CENTER.2.1 — per-venue column visibility for report tables.
 *
 * Frontend-only, like the guest shortlist: each table persists which columns
 * the user has shown/hidden under a `<prefix>:<venueId>` localStorage key.
 * Survives refresh, doesn't sync across devices, harmless if cleared.
 *
 * Effective visibility = defaults (computed by the caller — e.g. Location
 * hidden when the venue has ≤1 active location) OVERLAID with the user's saved
 * per-column choices. So a freshly-loaded venue follows the defaults; once the
 * user toggles a column that explicit choice wins and is remembered. Unknown /
 * newly-added columns always fall back to their default.
 *
 * Locked columns (identity/time anchors) are always visible and never written.
 */

export interface ColumnDef {
  key: string;
  label: string;
  /** Always visible; not offered in the menu as a toggle target. */
  locked?: boolean;
}

type VisibilityMap = Record<string, boolean>;

const KEY = (prefix: string, venueId: string): string => `${prefix}:${venueId}`;

const safeParse = (raw: string | null): VisibilityMap => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: VisibilityMap = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'boolean') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
};

/**
 * @param storagePrefix stable per-table prefix, e.g. "txRegister.cols"
 * @param venueId       scopes the persisted choice to the venue (null → no persistence)
 * @param columns       the column set (locked ones are forced visible)
 * @param defaults      per-key default visibility; memoize in the caller
 */
export const useColumnVisibility = (
  storagePrefix: string,
  venueId: string | null | undefined,
  columns: ColumnDef[],
  defaults: VisibilityMap,
): {
  visible: VisibilityMap;
  toggle: (key: string) => void;
  reset: () => void;
} => {
  const [overrides, setOverrides] = useState<VisibilityMap>({});

  // (Re)load saved choices when the venue changes.
  useEffect(() => {
    if (typeof window === 'undefined' || !venueId) { setOverrides({}); return; }
    setOverrides(safeParse(window.localStorage.getItem(KEY(storagePrefix, venueId))));
  }, [storagePrefix, venueId]);

  const persist = useCallback((next: VisibilityMap) => {
    if (typeof window === 'undefined' || !venueId) return;
    try {
      window.localStorage.setItem(KEY(storagePrefix, venueId), JSON.stringify(next));
    } catch {
      // QuotaExceeded / disabled storage — the choice just won't persist.
    }
  }, [storagePrefix, venueId]);

  const visible = useMemo<VisibilityMap>(() => {
    const out: VisibilityMap = {};
    for (const c of columns) {
      out[c.key] = c.locked
        ? true
        : (overrides[c.key] ?? defaults[c.key] ?? true);
    }
    return out;
  }, [columns, overrides, defaults]);

  const toggle = useCallback((key: string) => {
    setOverrides(prev => {
      const current = prev[key] ?? defaults[key] ?? true;
      const next = { ...prev, [key]: !current };
      persist(next);
      return next;
    });
  }, [defaults, persist]);

  const reset = useCallback(() => {
    setOverrides({});
    if (typeof window !== 'undefined' && venueId) {
      try { window.localStorage.removeItem(KEY(storagePrefix, venueId)); } catch { /* noop */ }
    }
  }, [storagePrefix, venueId]);

  return { visible, toggle, reset };
};
